import { apiGet, APIURL, cancelSelectEvent, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

const FORM_TYPE_OPTIONS = ['13F-HR', '13F-HR/A', '13F-NT', '13F-NT/A'] as const;
type FormTypeValue = (typeof FORM_TYPE_OPTIONS)[number];

interface FormTypeChartRow {
  form_type: string;
  total_filings: number;
  unique_institutions: number;
  earliest_filing?: string;
  latest_filing?: string;
  avg_filings_per_institution?: number;
}

interface FormTypesSummary {
  form1_filings: number;
  form2_filings: number;
  form1_institutions: number;
  form2_institutions: number;
  filings_difference: number;
  institutions_difference: number;
}

export class TraderverseFormTypesAndInstitutions {
  private container: HTMLElement;
  private config: FinancialConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: FormTypeChartRow[] = [];
  private currentSummary: FormTypesSummary | null = null;
  private currentChart: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
  private filters = {
    formType1: '13F-HR' as FormTypeValue,
    formType2: '13F-NT' as FormTypeValue,
  };

  constructor(config: FinancialConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    this.filters.formType1 = this.normalizeFormType(
      (config as any).formType1 ?? (this.config as any).formType1 ?? this.filters.formType1
    );
    this.filters.formType2 = this.normalizeFormType(
      (config as any).formType2 ?? (this.config as any).formType2 ?? this.filters.formType2
    );
    (this.config as any).formType1 = this.filters.formType1;
    (this.config as any).formType2 = this.filters.formType2;

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private getDefaultConfig(): Partial<FinancialConfig> {
    return {
      headerPinned: true,
      type: 'Cash and Debt',
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      formType1: '13F-HR',
      formType2: '13F-NT',
    } as any;
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private normalizeFormType(value: unknown): FormTypeValue {
    const raw = String(value ?? '13F-HR').trim().toUpperCase();
    const match = FORM_TYPE_OPTIONS.find(opt => opt.toUpperCase() === raw);
    return match ?? '13F-HR';
  }

  private renderFormTypeOptions(selected: FormTypeValue): string {
    return FORM_TYPE_OPTIONS
      .map(opt => `<option value="${opt}"${opt === selected ? ' selected' : ''}>${opt}</option>`)
      .join('');
  }

  private filtersIconSvg(): string {
    return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
  }

  private async init(): Promise<void> {
    this.attachStyles();
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    await this.setupChart();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
      themeClass = 'defaultLightMode';
      dataTheme = 'default-lite';
    }

    const legendSpans = `
      <span class="toggle-series active" data-key="filings" style="background-color:#FFEB3B;" title="Total Filings"></span>
      <span class="toggle-series active" data-key="institutions" style="background-color:#33EB91;" title="Unique Institutions"></span>
    `;

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()} Form Types And Institutions</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <div class="item">
              <button class="filters-toggle" type="button" aria-label="Toggle filters">${this.filtersIconSvg()}</button>
            </div>
          </div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            <div class="item">
              <div class="legendsCollection">
                ${legendSpans}
              </div>
            </div>
            <span></span>
            <div class="filtersBox sideBar hidden">
              <div class="item">
                <label>Form Type 1</label>
                <select class="formtype1-select" aria-label="Form Type 1" data-testing-id="formtype1-select">${this.renderFormTypeOptions(this.filters.formType1)}</select>
              </div>
              <div class="item">
                <label>Form Type 2</label>
                <select class="formtype2-select" aria-label="Form Type 2" data-testing-id="formtype2-select">${this.renderFormTypeOptions(this.filters.formType2)}</select>
              </div>
            </div>
          </div>
          <div class="chartDv" id="formTypesContainer">
            <div id="chartContainer" class="chart" data-testing-id="form-types-institutions-chart"></div>
          </div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
    this.attachLegendClickHandlers();
    this.wireFilterControls();
  }

  private attachLegendClickHandlers(): void {
    const legendSpans = this.shadowRoot.querySelectorAll('.toggle-series');
    legendSpans.forEach(legend => {
      legend.addEventListener('click', () => {
        legend.classList.toggle('active');
        this.filterSeriesByLegend();
      });
    });
  }

  private filterSeriesByLegend(): void {
    if (!this.currentChart || !this.currentData?.length) return;

    const activeKeys = Array.from(
      this.shadowRoot.querySelectorAll('.toggle-series.active')
    ).map(el => (el as HTMLElement).dataset.key);

    const categories = this.currentData.map(d => d.form_type);
    const filings = this.currentData.map(d => d.total_filings);
    const institutions = this.currentData.map(d => d.unique_institutions);

    this.currentChart.setOption({
      xAxis: { data: categories },
      series: [
        {
          name: 'Total Filings',
          type: 'bar',
          data: activeKeys.includes('filings') ? filings : [],
          barWidth: '28%',
          itemStyle: { color: '#FFEB3B', borderRadius: [4, 4, 0, 0] },
        },
        {
          name: 'Unique Institutions',
          type: 'bar',
          data: activeKeys.includes('institutions') ? institutions : [],
          barWidth: '28%',
          itemStyle: { color: '#33EB91', borderRadius: [4, 4, 0, 0] },
        },
      ],
    });
  }

  private wireFilterControls(): void {
    const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
    const formType1Select = this.shadowRoot.querySelector('.formtype1-select') as HTMLSelectElement | null;
    const formType2Select = this.shadowRoot.querySelector('.formtype2-select') as HTMLSelectElement | null;

    if (filterBtn && filtersBox) {
      filterBtn.addEventListener('click', (ev: MouseEvent) => {
        ev.stopPropagation();
        filtersBox.classList.toggle('hidden');
      });
      filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());

      this.outsideClickHandler = (ev: MouseEvent) => {
        const anyEv: any = ev;
        const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;
        const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
        if (!clickedInside && !filtersBox.classList.contains('hidden')) {
          filtersBox.classList.add('hidden');
        }
      };
      document.addEventListener('click', this.outsideClickHandler);
    }

    const applyFiltersAndFetch = () => {
      this.filters.formType1 = this.normalizeFormType(formType1Select?.value || this.filters.formType1);
      this.filters.formType2 = this.normalizeFormType(formType2Select?.value || this.filters.formType2);
      (this.config as any).formType1 = this.filters.formType1;
      (this.config as any).formType2 = this.filters.formType2;
      this.refetchChart();
    };

    formType1Select?.addEventListener('change', applyFiltersAndFetch);
    formType2Select?.addEventListener('change', applyFiltersAndFetch);
  }

  private refetchChart(): void {
    try {
      this.setupChart();
    } catch {
      /* ignore */
    }
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private extractChartData(responseData: any): FormTypeChartRow[] {
    let dataBlock = responseData?.data ?? responseData?.Data ?? responseData;
    if (typeof dataBlock === 'string') {
      try {
        dataBlock = JSON.parse(dataBlock);
      } catch {
        return [];
      }
    }
    if (!dataBlock || dataBlock.error) return [];

    const chartData = dataBlock?.chart_data ?? dataBlock?.form_types_institutions ?? responseData?.chart_data;
    if (!Array.isArray(chartData)) return [];

    return chartData.map((row: any) => ({
      form_type: String(row?.form_type ?? row?.formType ?? ''),
      total_filings: Number(row?.total_filings ?? row?.filings ?? 0) || 0,
      unique_institutions: Number(row?.unique_institutions ?? row?.institutions ?? 0) || 0,
      earliest_filing: row?.earliest_filing,
      latest_filing: row?.latest_filing,
      avg_filings_per_institution: row?.avg_filings_per_institution != null
        ? Number(row.avg_filings_per_institution)
        : undefined,
    })).filter((row: FormTypeChartRow) => !!row.form_type);
  }

  private extractSummary(responseData: any): FormTypesSummary | null {
    const summary = responseData?.data?.summary ?? responseData?.summary;
    if (!summary || typeof summary !== 'object') return null;
    return {
      form1_filings: Number(summary.form1_filings) || 0,
      form2_filings: Number(summary.form2_filings) || 0,
      form1_institutions: Number(summary.form1_institutions) || 0,
      form2_institutions: Number(summary.form2_institutions) || 0,
      filings_difference: Number(summary.filings_difference) || 0,
      institutions_difference: Number(summary.institutions_difference) || 0,
    };
  }

  private async setupChart(): Promise<void> {
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    try {
      if (widgetBox) widgetBox.classList.add('loading');

      const res = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/form-13f/types-institutions`,
        query: {
          formType1: this.filters.formType1,
          formType2: this.filters.formType2,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const json = await res.json();
      if (json && typeof json.isSuccess === 'boolean' && !json.isSuccess) {
        throw new Error('API returned isSuccess: false');
      }
      if (json?.data?.error) {
        throw new Error(String(json.data.error));
      }

      this.currentData = this.extractChartData(json);
      this.currentSummary = this.extractSummary(json);

      if (!this.currentData.length) {
        widgetBox?.removeAttribute('data-testing-id');
      }

      this.updateDisplay();
      this.emit('dataUpdate', { chart_data: this.currentData, summary: this.currentSummary });
    } catch (err) {
      console.error('Error fetching chart data:', err);
      widgetBox?.removeAttribute('data-testing-id');
      this.currentData = [];
      this.currentSummary = null;
      this.updateDisplay();
    } finally {
      widgetBox?.classList.remove('loading');
    }
  }

  private updateDisplay(): void {
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.drawChart();
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (!container) return;

    const instance = echarts.getInstanceByDom(container);
    if (instance) echarts.dispose(instance);

    if (!this.currentData.length) {
      widgetBox?.removeAttribute('data-testing-id');
      if (this.currentChart) {
        try { this.currentChart.dispose(); } catch { /* ignore */ }
        this.currentChart = null;
      }
      container.innerHTML = getNoDataHTML();
      return;
    }

    container.setAttribute('data-testing-id', 'Form-Types-And-Institutions');
    container.innerHTML = '';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.minHeight = '260px';

    const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
    const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
    const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
    const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');

    const categories = this.currentData.map(d => d.form_type);
    const filings = this.currentData.map(d => d.total_filings);
    const institutions = this.currentData.map(d => d.unique_institutions);

    const activeKeys = Array.from(
      this.shadowRoot.querySelectorAll('.toggle-series.active')
    ).map(el => (el as HTMLElement).dataset.key);

    const option = {
      backgroundColor: 'transparent',
      animation: false,
      tooltip: {
        ...getEChartsTooltipTheme(widgetBox),
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const list = Array.isArray(params) ? params : [params];
          const index = list[0]?.dataIndex ?? 0;
          const row = this.currentData[index];
          if (!row) return '';
          const lines = [
            `<strong>${row.form_type}</strong>`,
            `Total Filings: ${row.total_filings.toLocaleString()}`,
            `Unique Institutions: ${row.unique_institutions.toLocaleString()}`,
          ];
          if (row.avg_filings_per_institution != null) {
            lines.push(`Avg Filings / Institution: ${row.avg_filings_per_institution}`);
          }
          if (row.earliest_filing || row.latest_filing) {
            lines.push(`Range: ${row.earliest_filing ?? '—'} → ${row.latest_filing ?? '—'}`);
          }
          return lines.join('<br/>');
        },
      },
      grid: { left: 10, right: 12, top: 24, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: { color: textColor, fontSize: fontSize, margin: 12 },
        axisTick: { show: false },
        axisLine: { show: true, lineStyle: { color: axisLineColor } },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: textColor,
          fontSize: fontSize,
          formatter: (value: number) => value.toLocaleString(),
        },
        axisTick: { show: false },
        axisLine: { show: false, lineStyle: { color: axisLineColor } },
        splitLine: { show: true, lineStyle: { color: splitLineColor, type: 'dashed' } },
      },
      series: [
        {
          name: 'Total Filings',
          type: 'bar',
          data: activeKeys.includes('filings') ? filings : [],
          barWidth: '28%',
          itemStyle: { color: '#FFEB3B', borderRadius: [4, 4, 0, 0] },
        },
        {
          name: 'Unique Institutions',
          type: 'bar',
          data: activeKeys.includes('institutions') ? institutions : [],
          barWidth: '28%',
          itemStyle: { color: '#33EB91', borderRadius: [4, 4, 0, 0] },
        },
      ],
    };

    const chart = echarts.init(container);
    chart.setOption(option);
    this.currentChart = chart;
    requestAnimationFrame(() => chart.resize());
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.currentChart) {
      try { this.currentChart.dispose(); } catch { /* ignore */ }
      this.currentChart = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach(h => h(evt));
    }
  }

  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }
}

declare global { interface Window { TraderverseFormTypesAndInstitutions: typeof TraderverseFormTypesAndInstitutions; } }
if (typeof window !== 'undefined') { window.TraderverseFormTypesAndInstitutions = TraderverseFormTypesAndInstitutions; }
